import { useEffect, useState, useMemo } from 'react'

import { Card, Icon, SkeletonBox, Tooltip } from '@circlefin/components'
import { getDisplayRate } from '@features/fx/utils'
import { currencyToToken, tokenToCurrency } from '@shared/components/common'
import { FxQuoteType, useFxQuoteLazyQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { useTransferWithFx } from '../../../hooks/fx'

export const ExchangeRateCard: React.FC = () => {
  const { t } = useTranslation('modals.transfer')
  const { quote } = useTransferWithFx()
  const [displayRate, setDisplayRate] = useState<string>()
  const [fetchRate, { loading }] = useFxQuoteLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  })

  // We need to know if the currencies are equivalent (IE USDC -> USD) to know what strings to show
  const isSameCurrency = useMemo(
    () =>
      quote &&
      (currencyToToken(quote.from.currency) === quote.to.currency ||
        tokenToCurrency(quote.from.currency) === quote.to.currency),
    [quote],
  )

  // Calculate rate directly from the quote when possible
  const displayRateFromExistingQuote = useMemo(
    () => getDisplayRate(quote?.from.amount, quote?.to.amount),
    [quote],
  )

  useEffect(() => {
    // If currencies are the same USDC:USD or EURC:EUR, display rate will always be 1
    if (isSameCurrency) {
      setDisplayRate('1.0000')
      return
    }

    if (!quote) {
      setDisplayRate(t`fx.exchangeRateCard.tbd`)
      return
    }

    // If we can calculate the rate from existing quote data, use it
    if (displayRateFromExistingQuote) {
      setDisplayRate(displayRateFromExistingQuote)
      return
    }

    // Fall back to separate API call for standardized rate. We use 100 as the fromAmount to ensure the quote request is not rejected for the amount being too small
    const input = {
      from: { currency: quote.from.currency, amount: '100' },
      to: { currency: quote.to.currency },
      type: FxQuoteType.internal,
    }

    void fetchRate({
      variables: {
        input,
      },
      onCompleted: (data) => {
        const displayRate = getDisplayRate(
          data.fxQuote.from.amount,
          data.fxQuote.to.amount,
        )
        setDisplayRate(displayRate || t`fx.exchangeRateCard.tbd`)
      },
      onError: () => {
        setDisplayRate(t`fx.exchangeRateCard.tbd`)
      },
    })
  }, [fetchRate, t, isSameCurrency, quote, displayRateFromExistingQuote])

  return (
    <Card className="h-fit" variant="primary/sm">
      <Card.Content className="flex flex-col gap-5">
        <span className="flex items-center gap-1 type-body-sm-bold">
          {isSameCurrency ? (
            t`fx.exchangeRateCard.title.sameCurrency`
          ) : (
            <>
              {t`fx.exchangeRateCard.title.differentCurrency`}
              <Tooltip
                className="z-[70]"
                content={<span>{t`fx.exchangeRateCard.tooltip`}</span>}
                place="top"
                size="sm"
              >
                <span>
                  <Icon name="InformationCircleOutline" />
                </span>
              </Tooltip>
            </>
          )}
        </span>
        <SkeletonBox className="h-7 w-80" loading={loading}>
          <span className="text-lg text-neutral font-circular-bold">
            {quote &&
              t('fx.exchangeRateCard.marketExchangeRateDetail', {
                fromMoney: `1.00 ${quote.from.currency}`,
                toMoney: `${displayRate} ${quote.to.currency}`,
              })}
          </span>
        </SkeletonBox>
      </Card.Content>
    </Card>
  )
}
